import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Zap, Globe, ArrowDown, ChevronDown, ArrowRight, Lock, Percent, Headphones } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { cryptoApi } from '../utils/api';
import { flagUrl } from '../utils/currencies';
import CurrencyIcon from '../components/CurrencyIcon';
import Header from '../components/Header';
import CryptoTicker from '../components/CryptoTicker';
import Footer from '../components/Footer';
import FAQ from '../components/FAQ';
import ReviewsSection from '../components/ReviewsSection';
import { FadeUp, StaggerContainer, StaggerItem, Floating } from '../components/Motion';

const HERO_IMG = 'https://images.unsplash.com/photo-1598266643181-398af0105d6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxsaWZlc3R5bGUlMjBoYXBweSUyMHBlcnNvbiUyMHNreSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg3MDc0MTM0fDA&ixlib=rb-4.1.0&q=85';
const COINS_IMG = 'https://images.unsplash.com/photo-1624365169873-d42588f4e866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxnb2xkJTIwc2lsdmVyJTIwbWV0YWxsaWMlMjBjb2luc3xlbnwwfHx8fDE3ODcwNzQxMzR8MA&ixlib=rb-4.1.0&q=85';

const Landing = () => {
  const { t } = useLanguage();
  const [rates, setRates] = useState({});

  useEffect(() => {
    const fetchRates = async () => {
      try { const res = await cryptoApi.getRates(); setRates(res.data); } catch {}
    };
    fetchRates();
    const interval = setInterval(fetchRates, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <CryptoTicker />
      <Header />

      {/* ===== HERO — full-bleed photo, Revolut style ===== */}
      <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/40"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center py-24">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-white font-extrabold text-5xl sm:text-6xl lg:text-[88px] leading-[1.02] tracking-[-0.03em] mb-6"
            style={{ fontFamily: 'Manrope' }}
          >
            {t.hero?.title || "Bien plus qu'un simple échange"}{t.hero?.titleHighlight ? <> <span className="text-white">{t.hero.titleHighlight}</span></> : null}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-white/90 text-base md:text-lg max-w-2xl mx-auto mb-10"
          >
            {t.hero?.description || "Crypto, devises et transferts internationaux. Rapide, sécurisé, transparent — partout dans le monde."}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-4 mb-16"
          >
            <Link to="/exchange" data-testid="hero-exchange-btn">
              <button className="btn-pill-white px-8 py-4 text-base">{t.hero?.startBtn || 'Commencer un échange'}</button>
            </Link>
            <Link to="/send-money" data-testid="hero-send-btn">
              <button className="btn-pill-ghost-dark px-8 py-4 text-base backdrop-blur-md flex items-center space-x-2">
                <Globe className="w-5 h-5" />
                <span>{t.hero?.sendBtn || "Envoyer de l'argent"}</span>
              </button>
            </Link>
          </motion.div>

          {/* Floating glass balance card */}
          <Floating className="max-w-sm mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="glass-card p-8 text-center"
              data-testid="hero-glass-card"
            >
              <div className="text-white/80 text-sm mb-1 font-medium">Portefeuille</div>
              <div className="text-white text-5xl font-extrabold tracking-tight mb-4 font-mono-data">
                {rates.BTC?.usd ? `$${Math.round(rates.BTC.usd).toLocaleString('fr-FR')}` : '$98 742'}
              </div>
              <div className="inline-flex items-center space-x-2 bg-white rounded-full px-5 py-2 mb-6">
                <CurrencyIcon symbol="BTC" size={18} />
                <span className="text-sm font-bold text-gray-900">1 BTC</span>
                {rates.BTC?.change_24h !== undefined && (
                  <span className={`text-xs font-bold ${rates.BTC.change_24h >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                    {rates.BTC.change_24h >= 0 ? '+' : ''}{rates.BTC.change_24h.toFixed(2)}%
                  </span>
                )}
              </div>
              <div className="flex items-center justify-center space-x-3">
                {['ETH', 'USDT', 'SOL', 'XRP'].map((s) => (
                  <div key={s} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                    <CurrencyIcon symbol={s} size={22} />
                  </div>
                ))}
              </div>
            </motion.div>
          </Floating>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 animate-bounce">
          <ArrowDown className="w-6 h-6" />
        </div>
      </section>

      {/* ===== QUICK CALCULATOR — white section ===== */}
      <section className="py-24 md:py-32 px-6 bg-white" data-testid="calculator-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <FadeUp>
            <h2 className="text-gray-900 font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] mb-6" style={{ fontFamily: 'Manrope' }}>
              {t.common?.quickCalc || 'Calculez votre échange en direct'}
            </h2>
            <p className="text-gray-500 text-lg mb-8 max-w-md">
              {t.hero?.calcDesc || 'Taux actualisés toutes les 30 secondes. Bloquez votre prix pendant 10 minutes au moment de la transaction.'}
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <div className="flex items-center space-x-2"><Lock className="w-4 h-4" /><span>{t.hero?.rateLock || 'Taux bloqué 10 min'}</span></div>
              <div className="flex items-center space-x-2"><Zap className="w-4 h-4" /><span>{t.features?.speed?.title || 'Instantané'}</span></div>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <QuickCalculator t={t} rates={rates} />
          </FadeUp>
        </div>
      </section>

      {/* ===== DARK SECTION — transparency / commission ===== */}
      <section className="py-24 md:py-32 px-6 bg-black relative overflow-hidden" data-testid="dark-commission-section">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <FadeUp>
            <h2 className="text-white font-extrabold text-4xl md:text-6xl lg:text-[72px] leading-[1.05] tracking-[-0.02em] mb-6" style={{ fontFamily: 'Manrope' }}>
              {t.hero?.darkTitle || 'Une commission unique. Zéro surprise.'}
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">
              {t.hero?.darkDesc || '7% tout compris sur chaque opération — achat, vente, envoi. Pas de frais cachés, pas de mauvaise surprise à l\'arrivée.'}
            </p>
            <Link to="/exchange" data-testid="dark-section-cta">
              <button className="btn-pill-white px-8 py-4 text-base">{t.hero?.startBtn || 'Commencer un échange'}</button>
            </Link>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-16">
            <img src={COINS_IMG} alt="Coins" className="rounded-3xl w-full max-w-3xl mx-auto h-72 md:h-96 object-cover opacity-90" />
          </FadeUp>
        </div>
      </section>

      {/* ===== TRADING DIRECT PRESENTATION ===== */}
      <section className="py-24 md:py-32 px-6 bg-white" data-testid="trading-direct-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <FadeUp>
            <span className="inline-flex items-center space-x-2 gradient-pink-violet text-white px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span><span>{t.p2p?.badge || 'Nouveau'}</span>
            </span>
            <h2 className="text-gray-900 font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] mb-6" style={{ fontFamily: 'Manrope' }}>
              {t.p2p?.presentTitle || 'Tradez en direct, entre vous'}
            </h2>
            <p className="text-gray-500 text-lg mb-8 max-w-md">{t.p2p?.presentDesc || 'Un carnet d\'ordres peer-to-peer avec séquestre sécurisé : vos ordres restent en ligne jusqu\'à trouver une contrepartie.'}</p>
            <div className="space-y-3 mb-10">
              {[t.p2p?.how1, t.p2p?.how2, t.p2p?.how3].map((txt, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="w-7 h-7 rounded-full bg-[#191C1F] text-white text-xs font-extrabold flex items-center justify-center flex-shrink-0">{i + 1}</div>
                  <p className="text-gray-600 text-sm pt-1">{txt}</p>
                </div>
              ))}
            </div>
            <Link to="/trading" data-testid="trading-direct-cta">
              <button className="btn-pill-dark px-8 py-4 text-base inline-flex items-center space-x-2">
                <span>{t.p2p?.cta || 'Découvrir le Trading Direct'}</span><ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="bg-black rounded-[36px] p-8 relative overflow-hidden">
              <div className="text-white/50 text-sm mb-4 font-semibold">{t.p2p?.orderbook || "Carnet d'ordres"} · BTC/EUR</div>
              <div className="space-y-2.5">
                {[
                  { side: 'sell', amt: '0.25 BTC', px: rates.BTC?.eur ? Math.round(rates.BTC.eur * 1.01) : 91500 },
                  { side: 'buy', amt: '0.10 BTC', px: rates.BTC?.eur ? Math.round(rates.BTC.eur * 0.995) : 90100 },
                  { side: 'sell', amt: '1 500 USDT', px: 0.93, sym: 'USDT' },
                  { side: 'buy', amt: '0.05 BTC', px: rates.BTC?.eur ? Math.round(rates.BTC.eur * 0.99) : 89700 },
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-5 py-3.5">
                    <div className="flex items-center space-x-3">
                      <CurrencyIcon symbol={r.sym || 'BTC'} size={22} />
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${r.side === 'sell' ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'}`}>
                        {r.side === 'sell' ? (t.p2p?.sell || 'Vendre') : (t.p2p?.buy || 'Acheter')}
                      </span>
                      <span className="text-white font-mono-data text-sm font-bold">{r.amt}</span>
                    </div>
                    <span className="text-white/70 font-mono-data text-sm">{r.px.toLocaleString('fr-FR')} €</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center space-x-2 text-white/40 text-xs">
                <Shield className="w-3.5 h-3.5" /><span>{t.p2p?.escrowInfo || 'Fonds bloqués max 45 min'} · {t.p2p?.commission || 'Commission vendeur 7%'}</span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ===== BENTO FEATURES — white section with gradient cards ===== */}
      <section className="py-24 md:py-32 px-6 bg-[#F8F9FA]" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <h2 className="text-gray-900 font-extrabold text-4xl md:text-5xl tracking-[-0.02em] mb-16 max-w-2xl" style={{ fontFamily: 'Manrope' }}>
              {t.features?.title || 'Pourquoi choisir alphaswapp ?'}
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StaggerItem className="gradient-pink-violet rounded-3xl p-10 text-white min-h-[320px] flex flex-col justify-between md:col-span-2">
              <Globe className="w-10 h-10" strokeWidth={1.5} />
              <div>
                <h3 className="text-3xl font-extrabold mb-3 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.sendMoney?.promoTitle || "Envoyez de l'argent à l'international"}</h3>
                <p className="text-white/80 mb-6 max-w-md">{t.sendMoney?.promoDesc || 'Europe, Asie, Russie. Simple comme un virement — nous nous occupons de tout.'}</p>
                <div className="flex items-center space-x-2 mb-6">
                  {['fr', 'de', 'gb', 'ru', 'cn', 'jp', 'in'].map(c => (
                    <img key={c} src={flagUrl(c)} alt={c} className="w-8 h-8 rounded-full border-2 border-white/40" />
                  ))}
                </div>
                <Link to="/send-money" data-testid="feature-send-money-btn">
                  <button className="btn-pill-white px-6 py-3 text-sm inline-flex items-center space-x-2">
                    <span>{t.sendMoney?.cta || 'Envoyer maintenant'}</span><ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            </StaggerItem>
            <StaggerItem className="gradient-metal rounded-3xl p-10 text-white min-h-[320px] flex flex-col justify-between">
              <Shield className="w-10 h-10" strokeWidth={1.5} />
              <div>
                <h3 className="text-2xl font-extrabold mb-3 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.features?.security?.title || 'Un espace sécurisé'}</h3>
                <p className="text-white/60 text-sm">{t.features?.security?.description || 'Protection 24h/7j, chiffrement de niveau bancaire et vérification KYC.'}</p>
              </div>
            </StaggerItem>
            <StaggerItem className="bg-white rounded-3xl p-10 border border-gray-100 min-h-[280px] flex flex-col justify-between">
              <Zap className="w-10 h-10 text-[#191C1F]" strokeWidth={1.5} />
              <div>
                <h3 className="text-2xl font-extrabold mb-3 tracking-tight text-gray-900" style={{ fontFamily: 'Manrope' }}>{t.features?.speed?.title || 'Échanges instantanés'}</h3>
                <p className="text-gray-500 text-sm">{t.features?.speed?.description || 'Fonds reçus en 5 à 30 minutes grâce à notre système automatisé.'}</p>
              </div>
            </StaggerItem>
            <StaggerItem className="gradient-blue rounded-3xl p-10 text-white min-h-[280px] flex flex-col justify-between">
              <Percent className="w-10 h-10" strokeWidth={1.5} />
              <div>
                <h3 className="text-2xl font-extrabold mb-3 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.hero?.commissionTitle || '7% tout compris'}</h3>
                <p className="text-white/80 text-sm">{t.hero?.commissionDesc || 'Une seule commission transparente sur chaque opération. Affichée avant de valider.'}</p>
              </div>
            </StaggerItem>
            <StaggerItem className="bg-white rounded-3xl p-10 border border-gray-100 min-h-[280px] flex flex-col justify-between">
              <Headphones className="w-10 h-10 text-[#191C1F]" strokeWidth={1.5} />
              <div>
                <h3 className="text-2xl font-extrabold mb-3 tracking-tight text-gray-900" style={{ fontFamily: 'Manrope' }}>{t.features?.support?.title || 'Support 24/7'}</h3>
                <p className="text-gray-500 text-sm">{t.features?.support?.description || 'Une équipe disponible à tout moment pour vous accompagner.'}</p>
              </div>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* ===== STATS — black strip ===== */}
      <section className="py-20 px-6 bg-black" data-testid="stats-section">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
          {[
            { val: '50K+', label: t.stats?.activeUsers || 'Utilisateurs actifs' },
            { val: '€10M+', label: t.stats?.volumeExchanged || 'Volume échangé' },
            { val: '20+', label: t.stats?.cryptos || 'Cryptomonnaies' },
            { val: '5 min', label: t.stats?.avgTime || 'Temps moyen' },
          ].map((stat, idx) => (
            <FadeUp key={idx} delay={idx * 0.1}>
              <div className="text-white font-extrabold text-4xl md:text-6xl tracking-tight mb-2" style={{ fontFamily: 'Manrope' }}>{stat.val}</div>
              <div className="text-white/50 text-sm">{stat.label}</div>
            </FadeUp>
          ))}
        </div>
      </section>

      <ReviewsSection />
      <FAQ />
      <Footer />
    </div>
  );
};

/* ---- Quick Calculator (Revolut style, official logos) ---- */
const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'RUB', 'BTC', 'ETH', 'USDT', 'SOL', 'XRP'];

const CurrencyDropdown = ({ value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid={testid}
        className="flex items-center space-x-2 bg-white rounded-full px-4 py-2.5 border border-gray-200 hover:border-gray-400 transition-colors min-w-[110px]">
        <CurrencyIcon symbol={value} size={22} />
        <span className="font-bold text-gray-900 text-sm">{value}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl border border-gray-100 py-1.5 z-40 shadow-[0_16px_48px_rgb(0,0,0,0.12)] max-h-72 overflow-y-auto">
          {CURRENCIES.map(c => (
            <button key={c} onClick={() => { onChange(c); setOpen(false); }}
              data-testid={`${testid}-option-${c}`}
              className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center space-x-3 ${c === value ? 'bg-gray-50' : ''}`}>
              <CurrencyIcon symbol={c} size={22} />
              <span className="text-sm font-semibold text-gray-800">{c}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const QuickCalculator = ({ t, rates }) => {
  const [amount, setAmount] = useState('1000');
  const [fromCurrency, setFromCurrency] = useState('EUR');
  const [toCurrency, setToCurrency] = useState('BTC');

  const calculateReceived = () => {
    const fromPrice = rates[fromCurrency]?.usd || rates[fromCurrency]?.price || 1;
    const toPrice = rates[toCurrency]?.usd || rates[toCurrency]?.price || 1;
    if (!toPrice) return '0';
    const raw = (parseFloat(amount || 0) * fromPrice) / toPrice;
    const afterFee = raw * (1 - 0.07);
    return afterFee >= 1000 ? afterFee.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : afterFee >= 1 ? afterFee.toFixed(2) : afterFee.toFixed(6);
  };

  const commission = (parseFloat(amount || 0) * 0.07).toFixed(2);

  return (
    <div className="card-soft p-8" data-testid="quick-calculator">
      <div className="mb-4">
        <label className="text-sm text-gray-500 mb-2 block font-medium">{t?.exchangeWidget?.youGive || 'Vous envoyez'}</label>
        <div className="flex items-center bg-[#F5F6F7] rounded-2xl p-2 pl-5">
          <input
            type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-3xl font-bold text-gray-900 py-3 outline-none font-mono-data w-full min-w-0"
            placeholder="1000" data-testid="calc-amount-input"
          />
          <CurrencyDropdown value={fromCurrency} onChange={setFromCurrency} testid="calc-from-currency" />
        </div>
      </div>

      <div className="flex justify-center py-1">
        <div className="w-10 h-10 rounded-full bg-[#191C1F] flex items-center justify-center">
          <ArrowDown className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="mb-5">
        <label className="text-sm text-gray-500 mb-2 block font-medium">{t?.exchangeWidget?.youReceive || 'Vous recevez'}</label>
        <div className="flex items-center bg-[#F5F6F7] rounded-2xl p-2 pl-5">
          <div className="flex-1 text-3xl font-bold text-gray-900 py-3 font-mono-data truncate" data-testid="calc-result">
            {calculateReceived()}
          </div>
          <CurrencyDropdown value={toCurrency} onChange={setToCurrency} testid="calc-to-currency" />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-400 mb-6 px-1">
        <span>{t?.exchangeWidget?.commission || 'Commission'} : 7%</span>
        <span className="font-mono-data">{commission} {fromCurrency}</span>
      </div>

      <Link to="/exchange" className="block">
        <button className="w-full btn-pill-dark py-4 text-base" data-testid="calc-exchange-btn">
          {t?.exchangeWidget?.exchange || 'Échanger maintenant'}
        </button>
      </Link>
    </div>
  );
};

export default Landing;
